import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import logger from '../utils/logger';
import { auth } from '../auth/firebase';

export interface GoogleAdsMetric {
  _id?: string;
  date: string;
  ad_network_type?: string;
  campaign_id?: string;
  campaign_name?: string;
  campaign_status?: string;
  advertising_channel_type?: string;
  campaign_budget?: number;
  impressions: number;
  clicks: number;
  cost: number;
  average_cpc?: number;
  ctr: number;
  conversions: number;
  conversion_rate?: number;
  cost_per_conversion?: number;
  updated_at?: string;
}

export interface GoogleAdsCampaignSummary {
  campaign_id: string;
  campaign_name: string;
  campaign_status: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;
  avg_cpc: number;
  conversion_rate: number;
  cost_per_conversion: number;
}

export interface GoogleAdsMetricsResponse {
  account: string;
  period: string;
  total_records: number;
  total_campaigns?: number;
  metrics: GoogleAdsMetric[];
  campaign_summary?: GoogleAdsCampaignSummary[];
}

export interface PaidSearchCampaignMetric {
  campaignName: string;
  campaignId: string | null;
  spend: number;
  impressions: number;
  clicks: number;
  ctr: number;
  totalUsers: number;
  cpc: number;
  adsConversions: number;
  cac: number | null;
}

export interface PaidSearchCampaignResponse {
  account: string;
  period: string;
  channelGroup: 'Paid Search';
  dimension: 'firstUserCampaignName';
  joinDimension: 'firstUserGoogleAdsCampaignId';
  metric: 'totalUsers';
  totalCampaigns: number;
  data: PaidSearchCampaignMetric[];
  unattributed: PaidSearchCampaignMetric | null;
}

const toFiniteNumber = (value: unknown): number => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

// Temporary rolling-deployment fallback for responses from backend instances
// that do not yet include campaign_summary. New consumers should prefer the
// backend-provided summary so all pages use the same aggregation contract.
export const buildGoogleAdsCampaignSummaryFallback = (
  metrics: GoogleAdsMetric[]
): GoogleAdsCampaignSummary[] => {
  const orderedMetrics = [...metrics].sort((left, right) => {
    const dateOrder = String(right.date || '').localeCompare(String(left.date || ''));
    if (dateOrder !== 0) return dateOrder;
    return String(right.updated_at || '').localeCompare(String(left.updated_at || ''));
  });
  const campaigns = new Map<string, GoogleAdsCampaignSummary>();

  orderedMetrics.forEach((metric) => {
    const campaignName = String(metric.campaign_name || 'Unknown Campaign');
    const campaignId = String(metric.campaign_id || `name:${campaignName}`);
    const current = campaigns.get(campaignId) || {
      campaign_id: campaignId,
      campaign_name: campaignName,
      campaign_status: String(metric.campaign_status || 'UNKNOWN'),
      impressions: 0,
      clicks: 0,
      cost: 0,
      conversions: 0,
      ctr: 0,
      avg_cpc: 0,
      conversion_rate: 0,
      cost_per_conversion: 0,
    };

    current.impressions += toFiniteNumber(metric.impressions);
    current.clicks += toFiniteNumber(metric.clicks);
    current.cost += toFiniteNumber(metric.cost);
    current.conversions += toFiniteNumber(metric.conversions);
    campaigns.set(campaignId, current);
  });

  return Array.from(campaigns.values())
    .map((campaign) => ({
      ...campaign,
      ctr: campaign.impressions > 0 ? (campaign.clicks / campaign.impressions) * 100 : 0,
      avg_cpc: campaign.clicks > 0 ? campaign.cost / campaign.clicks : 0,
      conversion_rate: campaign.clicks > 0 ? (campaign.conversions / campaign.clicks) * 100 : 0,
      cost_per_conversion: campaign.conversions > 0 ? campaign.cost / campaign.conversions : 0,
    }))
    .sort((left, right) => left.campaign_name.localeCompare(right.campaign_name));
};

class ApiService {
  private api: AxiosInstance;
  private baseUrl: string;
  private inFlightGetRequests = new Map<string, Promise<any>>();
  private recentGetResponses = new Map<string, { expiresAt: number; data: any }>();
  private tokenRefreshPromise: Promise<string> | null = null;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE || '';
    this.api = axios.create({
      baseURL: this.baseUrl,
      timeout: 300000, // 5 minutes for large file uploads
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    // Add request interceptor to log URLs
    this.api.interceptors.request.use(
      (config) => {
        logger.debug(`API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    
    // Add response interceptor to handle 401 with token refresh
    this.api.interceptors.response.use(
      (response) => {
        logger.debug(`API Response: ${response.status} ${response.statusText}`);
        return response;
      },
      async (error) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
        
        logger.error('API Error:', error.response?.status, error.response?.data);
        
        // Priority 1: Handle 401 with token refresh and retry
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          logger.warn('Received 401 error, attempting to refresh token and retry...');
          
          try {
            const user = auth.currentUser;
            if (user) {
              // Share one forced refresh across requests that fail together
              // when a cached Firebase token expires.
              if (!this.tokenRefreshPromise) {
                this.tokenRefreshPromise = user.getIdToken(true).finally(() => {
                  this.tokenRefreshPromise = null;
                });
              }
              const newToken = await this.tokenRefreshPromise;
              this.setAuthToken(newToken);
              
              // Update the authorization header for the retry
              if (originalRequest.headers) {
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              }
              
              logger.info('Token refreshed successfully, retrying request...');
              
              // Retry the original request with new token
              return this.api(originalRequest);
            } else {
              logger.error('No authenticated user found for token refresh');
            }
          } catch (refreshError) {
            logger.error('Token refresh failed:', refreshError);
            // If refresh fails, reject with original error
            return Promise.reject(error);
          }
        }
        
        // ✅ If response data is a Blob and status indicates error, try to parse it
        if (error.response?.data instanceof Blob && error.response.status >= 400) {
          try {
            const text = await error.response.data.text();
            try {
              const errorData = JSON.parse(text);
              error.response.data = errorData;
              logger.error('Parsed error data:', errorData);
            } catch (parseError) {
              logger.error('Could not parse error response as JSON:', text);
            }
          } catch (readError) {
            logger.error('Could not read error response:', readError);
          }
        }
        
        return Promise.reject(error);
      }
    );
    
    // We're using Microsoft login via Firebase Auth, so no test token needed
    logger.debug('API Service initialized - waiting for authentication token');
  }

  setAuthToken(token: string) {
    logger.debug('Setting auth token');
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
  
  clearAuthToken() {
    logger.debug('Clearing auth token');
    delete this.api.defaults.headers.common['Authorization'];
  }

  isAuthenticated(): boolean {
    return !!this.api.defaults.headers.common['Authorization'];
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  getAuthHeader(): string | undefined {
    const authHeader = this.api.defaults.headers.common['Authorization'];
    return typeof authHeader === 'string' ? authHeader : undefined;
  }

  /**
   * Get a valid, fresh token. Proactively refreshes if token expires within 5 minutes.
   * Priority 2: Proactive token refresh to prevent 401 errors.
   */
  async getValidToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) {
      logger.warn('No authenticated user found');
      return null;
    }

    try {
      // Get token result with expiration time
      const tokenResult = await user.getIdTokenResult();
      const expirationTime = new Date(tokenResult.expirationTime).getTime();
      const now = Date.now();
      const fiveMinutesInMs = 5 * 60 * 1000;

      // Refresh if token expires in less than 5 minutes
      if (expirationTime - now < fiveMinutesInMs) {
        const timeUntilExpiry = Math.floor((expirationTime - now) / 60000);
        logger.info(`Token expiring in ${timeUntilExpiry} minutes, refreshing proactively...`);
        const newToken = await user.getIdToken(true); // Force refresh
        this.setAuthToken(newToken);
        return newToken;
      }

      // Token is still fresh
      return await user.getIdToken();
    } catch (error) {
      logger.error('Error checking/refreshing token:', error);
      return null;
    }
  }

  /**
   * Log token status for debugging token expiration issues.
   * Priority 2: Token expiration logging.
   */
  async logTokenStatus(): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      logger.debug('No authenticated user for token status check');
      return;
    }

    try {
      const tokenResult = await user.getIdTokenResult();
      const expirationTime = new Date(tokenResult.expirationTime);
      const issuedAt = new Date(tokenResult.issuedAtTime);
      const now = new Date();
      const timeUntilExpiry = Math.floor((expirationTime.getTime() - now.getTime()) / 60000);
      const tokenAge = Math.floor((now.getTime() - issuedAt.getTime()) / 60000);

      logger.debug('Token Status:', {
        issuedAt: issuedAt.toISOString(),
        expiresAt: expirationTime.toISOString(),
        timeUntilExpiry: `${timeUntilExpiry} minutes`,
        age: `${tokenAge} minutes`,
        isExpiringSoon: timeUntilExpiry < 5
      });
    } catch (error) {
      logger.error('Error logging token status:', error);
    }
  }

  private buildSearchParams(params?: Record<string, any>): URLSearchParams | undefined {
    if (!params) {
      return undefined;
    }

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((entry) => {
          if (entry !== undefined && entry !== null && entry !== '') {
            searchParams.append(key, String(entry));
          }
        });
      } else if (value !== '') {
        searchParams.append(key, String(value));
      }
    });

    return searchParams;
  }

  private buildGetCacheKey(url: string, config?: AxiosRequestConfig): string {
    const params = config?.params ?? {};
    const serializedParams = JSON.stringify(
      Object.keys(params)
        .sort()
        .reduce<Record<string, any>>((acc, key) => {
          acc[key] = params[key];
          return acc;
        }, {})
    );
    return `${url}::${serializedParams}`;
  }

  clearRecentGetCache(predicate?: (cacheKey: string) => boolean) {
    if (!predicate) {
      this.recentGetResponses.clear();
      return;
    }
    Array.from(this.recentGetResponses.keys()).forEach((cacheKey) => {
      if (predicate(cacheKey)) {
        this.recentGetResponses.delete(cacheKey);
      }
    });
  }

  clearInFlightGetRequests(predicate?: (cacheKey: string) => boolean) {
    if (!predicate) {
      this.inFlightGetRequests.clear();
      return;
    }
    Array.from(this.inFlightGetRequests.keys()).forEach((cacheKey) => {
      if (predicate(cacheKey)) {
        this.inFlightGetRequests.delete(cacheKey);
      }
    });
  }

  clearDashboardAccountCache(account: string) {
    const normalizedAccount = String(account || '').trim().toLowerCase();
    if (!normalizedAccount) {
      return;
    }
    const accountCacheMatcher = (cacheKey: string) => cacheKey.toLowerCase().includes(normalizedAccount);
    this.clearRecentGetCache(accountCacheMatcher);
    this.clearInFlightGetRequests(accountCacheMatcher);
  }

  clearLinkedInCache(account?: string) {
    if (account) {
      const normalizedAccount = String(account || '').trim().toLowerCase();
      const linkedinCacheMatcher = (cacheKey: string) => 
        cacheKey.toLowerCase().includes('/linkedin/') && cacheKey.toLowerCase().includes(normalizedAccount);
      this.clearRecentGetCache(linkedinCacheMatcher);
      this.clearInFlightGetRequests(linkedinCacheMatcher);
    } else {
      const linkedinCacheMatcher = (cacheKey: string) => cacheKey.toLowerCase().includes('/linkedin/');
      this.clearRecentGetCache(linkedinCacheMatcher);
      this.clearInFlightGetRequests(linkedinCacheMatcher);
    }
  }

  private async cachedGet<T = any>(
    url: string,
    config?: AxiosRequestConfig,
    ttlMs: number = 30000,
    skipCache: boolean = false
  ): Promise<T> {
    const cacheKey = this.buildGetCacheKey(url, config);
    const now = Date.now();
    
    // Only use cached response if skipCache is false
    if (!skipCache) {
      const cached = this.recentGetResponses.get(cacheKey);
      if (cached && cached.expiresAt > now) {
        return cached.data as T;
      }
    }

    const inFlight = this.inFlightGetRequests.get(cacheKey);
    if (inFlight && !skipCache) {
      return inFlight as Promise<T>;
    }

    const request = this.api.get(url, config)
      .then((response) => {
        this.recentGetResponses.set(cacheKey, {
          expiresAt: Date.now() + ttlMs,
          data: response.data,
        });
        return response.data as T;
      })
      .finally(() => {
        this.inFlightGetRequests.delete(cacheKey);
      });

    this.inFlightGetRequests.set(cacheKey, request);
    return request;
  }

  // Authentication endpoints
  async ping(idToken: string) {
  const response = await this.api.get('/secured/ping', {
      headers: { 'Authorization': `Bearer ${idToken}` }
    });
    return response.data;
  }

  // Dashboard endpoints
  async getDashboardMetrics(clientId?: string) {
  const response = await this.api.get('/dashboard/metrics', {
      params: { client_id: clientId }
    });
    return response.data;
  }

  async getDashboardLayout(account: string) {
    const response = await this.api.get('/dashboard-layout', { params: { account } });
    return response.data;
  }

  async getDashboardWidgetRegistry(source?: string, includeDisabled: boolean = false) {
    const response = await this.api.get('/dashboard-widget-registry', {
      params: {
        include_disabled: includeDisabled,
        ...(source ? { source } : {}),
      },
    });
    return response.data;
  }

  async saveDashboardLayout(
    account: string,
    layouts: Record<string, { colSpan: number; height: number; hidden: boolean; [key: string]: unknown }>,
    settings?: Record<string, unknown>,
  ) {
    const response = await this.api.put('/dashboard-layout', { account, layouts, settings });
    return response.data;
  }

  // Prospects endpoints
  async uploadProspectsCSV(formData: FormData) {
  const response = await this.api.post('/prospects/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async uploadProspectsCSVAsync(formData: FormData) {
    const response = await this.api.post('/prospects/upload-async', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getUploadStatus(uploadId: string) {
    const response = await this.api.get(`/prospects/upload-status/${uploadId}`);
    return response.data;
  }

  async listUploadStatuses(params?: Record<string, any>) {
    const response = await this.api.get('/prospects/upload-status', {
      params,
    });
    return response.data;
  }

  async acknowledgeUploadStatus(uploadId: string) {
    const response = await this.api.post(`/prospects/upload-status/${uploadId}/acknowledge`);
    return response.data;
  }

  async getProspects(filters?: any) {
  const response = await this.api.get('/prospects', {
      params: filters
    });
    return response.data;
  }

  async validateEmails(account?: string, limit?: number) {
    logger.debug(
      'API Service: validateEmails called',
      account ? `for account: ${account}` : 'for accessible accounts'
    );
    const formData = new FormData();
    if (account) {
      formData.append('account', account);
    }
    if (typeof limit === 'number') {
      formData.append('limit', String(limit));
    }
    const response = await this.api.post('/prospects/validate-emails', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    logger.debug('API Service: validateEmails response:', response.data);
    return response.data;
  }

  async listValidationJobs(params?: { status?: string; limit?: number; offset?: number; include_acknowledged?: boolean }) {
    const response = await this.api.get('/prospects/email-validation/jobs', {
      params,
    });
    return response.data;
  }

  async getValidationJob(jobId: string) {
    const response = await this.api.get(`/prospects/email-validation/jobs/${jobId}`);
    return response.data;
  }

  async acknowledgeValidationJob(jobId: string) {
    const response = await this.api.post(`/prospects/email-validation/jobs/${jobId}/acknowledge`);
    return response.data;
  }

  async getTemplateTypes() {
  try {
      logger.debug('[API] Fetching template types...');
      const response = await this.api.get('/prospects/template-types');
      logger.debug('[API] Template types fetched successfully');
      return response.data;
    } catch (error) {
      logger.error('[API] Error fetching template types:', error);
      throw error;
    }
  }
  
  async getProspectsClients() {
  try {
      logger.debug('[API] Fetching prospects clients...');
      const response = await this.api.get('/clients');
      logger.debug('[API] Prospects clients fetched successfully');
      return response.data;
    } catch (error) {
      logger.error('[API] Error fetching prospects clients:', error);
      throw error;
    }
  }
  
  async generateAudienceTemplate(templateType: string, clientId: string, emailQuality?: string) {
    try {
      const formData = new FormData();
      formData.append('template_type', templateType);
      formData.append('client_id', clientId);
      
      if (templateType === 'MailerCloud' && emailQuality && emailQuality !== 'all') {
        formData.append('email_quality', emailQuality);
      }
      
    const response = await this.api.post('/prospects/generate-template', formData, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error: any) {
      logger.error('[API] Error generating template:', error);
      
      // ✅ Handle error responses
      if (error.response?.data) {
        // Check if response data is a Blob
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            logger.debug('[API] Blob error text:', text);
            
            try {
              const errorData = JSON.parse(text);
              logger.debug('[API] Parsed error data:', errorData);
              
              // ✅ Check both 'detail' (FastAPI standard) and 'error' (custom)
              const errorMessage = errorData.detail || errorData.error || errorData.message || 'Failed to generate template';
              logger.debug('[API] Throwing error message:', errorMessage);
              throw new Error(errorMessage);
            } catch (parseError) {
              logger.error('[API] Could not parse Blob as JSON');
              throw new Error(`Failed to generate template: ${error.response.status} ${error.response.statusText}`);
            }
          } catch (readError) {
            logger.error('[API] Could not read Blob:', readError);
            throw new Error('Failed to generate template');
          }
        } else {
          // Response data is already parsed JSON
          // ✅ Check both 'detail' and 'error' fields
          const errorMessage = error.response.data?.detail 
            || error.response.data?.error 
            || error.response.data?.message 
            || error.message 
            || 'Failed to generate template';
          logger.debug('[API] Error message from JSON response:', errorMessage);
          throw new Error(errorMessage);
        }
      }
      
      // If error.message is already set, re-throw
      if (error.message) {
        throw error;
      }
      
      throw new Error('Failed to generate template');
    }
  }

  // Generate audience template with search filters
  async generateAudienceTemplateWithFilters(templateType: string, filters: any) {
    try {
      const formData = new FormData();
      formData.append('template_type', templateType);
      
      // Add all filter parameters
      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((entry) => {
            if (entry) {
              formData.append(key, String(entry));
            }
          });
        } else if (value) {
          formData.append(key, String(value));
        }
      });
      
      const response = await this.api.post('/prospects/generate-template', formData, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error: any) {
      logger.error('[API] Error generating template with filters:', error);
      
      // Handle error responses
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          const errorMessage = errorData.detail || errorData.error || errorData.message || 'Failed to generate template';
          throw new Error(errorMessage);
        } catch (parseError) {
          throw new Error(`Failed to generate template: ${error.response.status} ${error.response.statusText}`);
        }
      } else {
        const errorMessage = error.response?.data?.detail 
          || error.response?.data?.error 
          || error.response?.data?.message 
          || error.message 
          || 'Failed to generate template';
        throw new Error(errorMessage);
      }
    }
  }

  // Campaign endpoints
  async getCampaigns(filters?: any) {
    const response = await this.api.get('/campaigns', {
      params: filters
    });
    return response.data;
  }

  // Email Campaign endpoints - Updated for new email campaigns service
  async getEmailCampaignAccounts() {
    try {
      logger.debug('Fetching email campaign accounts');
      const response = await this.api.get('/email-campaigns/accounts');
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch email campaign accounts:', error);
      throw error;
    }
  }

  async getEmailCampaignSummary(fromDate: string, toDate: string, account: string, forceRefresh: boolean = false) {
    try {
      logger.debug(`Fetching email campaign summary: ${fromDate} to ${toDate} for ${account}`);
      const params = {
        from_date: fromDate,
        to_date: toDate,
        account: account,
        force_refresh: forceRefresh
      };
      const response = await this.api.get('/email-campaigns/summary', { params });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch email campaign summary:', error);
      throw error;
    }
  }

  async syncEmailCampaignEvents(): Promise<any> {
    throw new Error('Email campaign event synchronization has been retired');
  }

  async getSyncStatus(): Promise<any> {
    return {
      status: 'disabled',
      is_running: false,
      message: 'Email campaign event synchronization has been retired',
    };
  }

  async getEmailCampaignMetrics(account?: string) {
    try {
      logger.debug(`Fetching email campaign metrics for ${account || 'all accounts'}`);
      const params = account ? { account } : {};
      const response = await this.api.get('/email-campaigns/metrics', { params });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch email campaign metrics:', error);
      throw error;
    }
  }

  async getCampaignDetails(campaignId: string) {
    const response = await this.api.get(`/campaigns/${campaignId}/details`);
    return response.data;
  }

  // Lead score endpoint (main leads API is in the Leads section below)
  async getLeadScore(prospectId: string) {
    const response = await this.api.get(`/leads/${prospectId}/score`);
    return response.data;
  }

  // Client CRUD endpoints - updated to match API Gateway routes
  async getClients() {
    try {
      logger.debug('[API] Fetching clients from /clients');
      const data = await this.cachedGet('/clients', undefined, 45000);
      logger.debug('[API] Successfully fetched clients:', data);
      // Support both legacy `{ clients: [...] }` responses and the current
      // admin-config-service raw `[...]` list response without breaking callers.
      if (Array.isArray(data)) {
        return data;
      }
      if (data && Array.isArray((data as { clients?: unknown[] }).clients)) {
        return (data as { clients: unknown[] }).clients;
      }
      logger.warn('[API] Unexpected /clients response shape, returning empty list');
      return [];
    } catch (error) {
      logger.error('[API] Failed to fetch clients:', error);
      throw error;
    }
  }
  
  async getClient(accountId: string) {
    try {
      logger.debug(`[API] Fetching client with account ID: ${accountId}`);
      return await this.cachedGet(`/clients/${accountId}`, undefined, 45000);
    } catch (error) {
      logger.error(`[API] Failed to fetch client ${accountId}:`, error);
      throw error;
    }
  }
  
  async createClient(client: any) {
    try {
      logger.debug('Creating client via /clients', client);
  const response = await this.api.post('/clients', client);
      logger.debug('Client created successfully:', response.data);
      return response.data;
    } catch (error) {
      logger.error('Failed to create client:', error);
      throw error;
    }
  }
  
  async updateClient(accountId: string, client: any) {
    try {
      logger.debug(`Updating client ${accountId}:`, client);
  const response = await this.api.put(`/clients/${accountId}`, client);
      logger.debug('Client updated successfully:', response.data);
      return response.data;
    } catch (error) {
      logger.error(`Failed to update client ${accountId}:`, error);
      throw error;
    }
  }
  
  async deleteClient(accountId: string) {
    try {
      logger.debug(`Deleting client with account ID: ${accountId}`);
  const response = await this.api.delete(`/clients/${accountId}`);
      return response.status === 204;
    } catch (error) {
      logger.error(`Failed to delete client ${accountId}:`, error);
      throw error;
    }
  }

  // Report generation endpoints
  async generateReport(reportType: string, format: string, filters?: any) {
    const response = await this.api.post('/reports/generate', {
      report_type: reportType,
      format: format,
      filters: filters
    });
    return response.data;
  }

  async downloadReport(reportId: string) {
    const response = await this.api.get(`/reports/${reportId}/download`, {
      responseType: 'blob'
    });
    return response;
  }

  // Feature flags endpoint
  async getFeatureFlags() {
    try {
      logger.debug('Fetching feature flags');
      const response = await this.api.get('/feature-flags');
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch feature flags:', error);
      throw error;
    }
  }

  // Email Metrics endpoints
  async getEmailMetricsAccounts() {
    try {
      logger.debug('Fetching email metrics accounts');
      const response = await this.api.get('/email-metrics/accounts');
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch email metrics accounts:', error);
      throw error;
    }
  }

  async getEmailMetricsPerformance(
    account: string, 
    period: string,
    page: number = 1,
    pageSize: number = 10,
    sortBy: string = 'opens',
    sortOrder: string = 'desc',
    campaignFilter: string = ''
  ) {
    try {
      logger.debug(`Fetching email metrics performance for ${account} (${period})`);
      const response = await this.api.get(`/email-metrics/performance/${account}`, {
        params: { 
          period,
          page,
          page_size: pageSize,
          sort_by: sortBy,
          sort_order: sortOrder,
          campaign_filter: campaignFilter
        }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch email metrics performance:', error);
      throw error;
    }
  }

  async getEmailMetricsAudienceSegmentation(account: string, segmentType: string = 'job_title', period: string) {
    try {
      logger.debug(`Fetching email metrics audience segmentation for ${account} (${segmentType}, ${period})`);
      const response = await this.api.get(`/email-metrics/audience-segmentation/${account}`, {
        params: { segment_type: segmentType, period }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch email metrics audience segmentation:', error);
      throw error;
    }
  }

  async getEmailMetricsMOFU(account: string, period: string) {
    try {
      logger.debug(`Fetching email metrics MOFU for ${account} (${period})`);
      const response = await this.api.get(`/email-metrics/mofu/${account}`, {
        params: { period }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch email metrics MOFU:', error);
      throw error;
    }
  }

  async getEmailMetricsLeadScoresByCampaign(account: string, period: string) {
    try {
      logger.debug(`Fetching email metrics lead scores by campaign for ${account} (${period})`);
      const response = await this.api.get(`/email-metrics/lead-scores-by-campaign/${account}`, {
        params: { period }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch email metrics lead scores by campaign:', error);
      throw error;
    }
  }

  // Google Analytics Methods
  async getGoogleAnalyticsAccounts() {
    try {
      logger.debug('Fetching Google Analytics accounts');
      return await this.cachedGet('/google-analytics/accounts', undefined, 45000);
    } catch (error) {
      logger.error('Failed to fetch Google Analytics accounts:', error);
      throw error;
    }
  }

  async syncGA4Data(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Syncing GA4 data for ${account} from ${startDate} to ${endDate}`);
      const response = await this.api.post(`/google-analytics/sync/ga4/${account}`, null, {
        params: { start_date: startDate, end_date: endDate }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to sync GA4 data:', error);
      throw error;
    }
  }

  async syncGoogleAdsData(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Syncing Google Ads data for ${account} from ${startDate} to ${endDate}`);
      const response = await this.api.post(`/google-analytics/sync/google-ads/${account}`, null, {
        params: { start_date: startDate, end_date: endDate }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to sync Google Ads data:', error);
      throw error;
    }
  }

  async syncGoogleAdsHourlyData(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Syncing Google Ads hourly data for ${account} from ${startDate} to ${endDate}`);
      const response = await this.api.post(`/google-analytics/sync/google-ads-hourly/${account}`, null, {
        params: { start_date: startDate, end_date: endDate }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to sync Google Ads hourly data:', error);
      throw error;
    }
  }

  async syncGoogleAdsDeviceData(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Syncing Google Ads device data for ${account} from ${startDate} to ${endDate}`);
      const response = await this.api.post(`/google-analytics/sync/google-ads-device/${account}`, null, {
        params: { start_date: startDate, end_date: endDate }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to sync Google Ads device data:', error);
      throw error;
    }
  }

  async syncGoogleAdsKeywordsData(account: string, startDate: string, endDate: string, limit: number = 100) {
    try {
      logger.debug(`Syncing Google Ads keywords data for ${account} from ${startDate} to ${endDate} (top ${limit})`);
      const response = await this.api.post(`/google-analytics/sync/google-ads-keywords/${account}`, null, {
        params: { start_date: startDate, end_date: endDate, limit }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to sync Google Ads keywords data:', error);
      throw error;
    }
  }

  async syncGoogleAdsBillingData(account: string, date: string) {
    try {
      logger.debug(`Syncing Google Ads billing data for ${account} on ${date}`);
      const response = await this.api.post(`/google-analytics/sync/google-ads-billing/${account}`, null, {
        params: { date }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to sync Google Ads billing data:', error);
      throw error;
    }
  }

  async getGA4DataAvailability(account: string) {
    try {
      logger.debug(`Fetching GA4 data availability for ${account}`);
      return await this.cachedGet(`/google-analytics/ga4-data-availability/${account}`, undefined, 45000);
    } catch (error) {
      logger.error('Failed to fetch GA4 data availability:', error);
      throw error;
    }
  }

  async getGA4Metrics(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Fetching GA4 metrics for ${account} from ${startDate} to ${endDate}`);
      return await this.cachedGet(`/google-analytics/ga4-metrics/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch GA4 metrics:', error);
      throw error;
    }
  }

  async getGA4SummaryTotals(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Fetching GA4 summary totals for ${account} from ${startDate} to ${endDate}`);
      return await this.cachedGet(`/google-analytics/ga4-summary-totals/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch GA4 summary totals:', error);
      throw error;
    }
  }

  async getGA4MetricsByPage(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`[API] Fetching GA4 metrics by page for ${account} from ${startDate} to ${endDate}`);
      const data = await this.cachedGet(`/google-analytics/ga4-aggregated/by-page/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      }, 30000);
      logger.debug(`[API] GA4 by-page response:`, data);
      return data;
    } catch (error) {
      logger.error('Failed to fetch GA4 metrics by page:', error);
      throw error;
    }
  }

  async getGA4LandingPageViewsByCountry(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`[API] Fetching GA4 landing page views by country for ${account}`);
      return await this.cachedGet(`/google-analytics/ga4-aggregated/landing-page-views-by-country/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch GA4 landing page views by country:', error);
      throw error;
    }
  }

  async getGA4MetricsByChannel(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Fetching GA4 metrics aggregated by channel for ${account}`);
      const response = await this.api.get(`/google-analytics/ga4-aggregated/by-channel/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch GA4 metrics by channel:', error);
      throw error;
    }
  }

  async getGA4MetricsByFirstUserChannel(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Fetching GA4 metrics aggregated by first user channel for ${account}`);
      const response = await this.api.get(`/google-analytics/ga4-aggregated/by-first-user-channel/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch GA4 metrics by first user channel:', error);
      throw error;
    }
  }

  async getGA4WeeklyUserAcquisition(account: string, endDate: string) {
    try {
      logger.debug(`Fetching eight-week GA4 user-acquisition traffic for ${account} through ${endDate}`);
      return await this.cachedGet(`/google-analytics/ga4-aggregated/weekly-user-acquisition/${account}`, {
        params: { end_date: endDate }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch weekly GA4 user-acquisition traffic:', error);
      throw error;
    }
  }

  async getPaidSearchCampaigns(account: string, startDate: string, endDate: string): Promise<PaidSearchCampaignResponse> {
    try {
      logger.debug(`Fetching Paid Search campaign acquisition for ${account} from ${startDate} to ${endDate}`);
      return await this.cachedGet<PaidSearchCampaignResponse>(
        `/google-analytics/ga4-aggregated/paid-search-campaigns/${account}`,
        { params: { start_date: startDate, end_date: endDate } },
        30000
      );
    } catch (error) {
      logger.error('Failed to fetch Paid Search campaign acquisition:', error);
      throw error;
    }
  }

  async getGA4MetricsByDevice(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Fetching GA4 metrics aggregated by device for ${account}`);
      return await this.cachedGet(`/google-analytics/ga4-aggregated/by-device/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch GA4 metrics by device:', error);
      throw error;
    }
  }

  async getGA4MetricsByCountry(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Fetching GA4 metrics aggregated by country for ${account}`);
      return await this.cachedGet(`/google-analytics/ga4-aggregated/by-country/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch GA4 metrics by country:', error);
      throw error;
    }
  }

  async getGA4MetricsByCity(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Fetching GA4 metrics aggregated by city for ${account}`);
      return await this.cachedGet(`/google-analytics/ga4-aggregated/by-city/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch GA4 metrics by city:', error);
      throw error;
    }
  }

  async getGA4WeeklyStats(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Fetching GA4 weekly stats for ${account}`);
      return await this.cachedGet(`/google-analytics/ga4-aggregated/weekly/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch GA4 weekly stats:', error);
      throw error;
    }
  }

  async getGA4MetricsBySourceMedium(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Fetching GA4 metrics aggregated by source/medium for ${account}`);
      return await this.cachedGet(`/google-analytics/ga4-aggregated/by-source-medium/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch GA4 metrics by source/medium:', error);
      throw error;
    }
  }

  async getGA4PagesBySourceMediumTime(account: string, startDate: string, endDate: string) {
    return await this.cachedGet(`/google-analytics/ga4-aggregated/by-source-medium-time/${account}`, {
      params: { start_date: startDate, end_date: endDate }
    }, 30000);
  }

  async getGA4DimensionTimeseries(
    account: string,
    dimension: 'page' | 'device' | 'source_medium' | 'first_user_channel' | 'country' | 'city',
    startDate: string,
    endDate: string,
    limit: number = 6
  ) {
    return await this.cachedGet(`/google-analytics/ga4-aggregated/timeseries/${account}`, {
      params: { dimension, start_date: startDate, end_date: endDate, limit }
    }, 30000);
  }

  // Google Ads Extended Methods
  async getGoogleAdsHourlyPerformance(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Fetching Google Ads hourly performance for ${account}`);
      const response = await this.api.get(`/google-analytics/google-ads/hourly-performance/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch Google Ads hourly performance:', error);
      throw error;
    }
  }

  async getGoogleAdsDeviceBreakdown(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Fetching Google Ads device breakdown for ${account}`);
      return await this.cachedGet(`/google-analytics/google-ads/device-breakdown/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch Google Ads device breakdown:', error);
      throw error;
    }
  }

  async getGoogleAdsDeviceBreakdownTimeseries(account: string, startDate: string, endDate: string, limit: number = 6) {
    try {
      logger.debug(`Fetching Google Ads device breakdown timeseries for ${account}`);
      return await this.cachedGet(`/google-analytics/google-ads/device-breakdown-timeseries/${account}`, {
        params: { start_date: startDate, end_date: endDate, limit }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch Google Ads device breakdown timeseries:', error);
      throw error;
    }
  }

  async getGoogleAdsKeywords(account: string, startDate: string, endDate: string, limit: number = 100) {
    try {
      logger.debug(`Fetching Google Ads keywords for ${account} (top ${limit})`);
      const response = await this.api.get(`/google-analytics/google-ads/keywords/${account}`, {
        params: { start_date: startDate, end_date: endDate, limit }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch Google Ads keywords:', error);
      throw error;
    }
  }

  async getGoogleAdsBillingData(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Fetching Google Ads billing data for ${account} from ${startDate} to ${endDate}`);
      const response = await this.api.get(`/google-analytics/google-ads/billing/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch Google Ads billing data:', error);
      throw error;
    }
  }

  // CSV Export Methods
  async exportGA4ByPageCSV(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`[API_CSV] Exporting GA4 metrics by page for ${account} from ${startDate} to ${endDate}`);
      const response = await this.api.get(`/google-analytics/ga4-aggregated/by-page/${account}/export`, {
        params: { start_date: startDate, end_date: endDate },
        responseType: 'blob'
      });
      logger.debug(`[API_CSV] Export response received - status: ${response.status}, blob size: ${response.data.size} bytes`);
      logger.debug(`[API_CSV] Response headers:`, response.headers);
      return response.data;
    } catch (error: any) {
      logger.error('[API_CSV] Failed to export GA4 metrics by page');
      logger.error('[API_CSV] Error status:', error.response?.status);
      logger.error('[API_CSV] Error data:', error.response?.data);
      logger.error('[API_CSV] Error message:', error.message);
      throw error;
    }
  }

  async exportGA4ByChannelCSV(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Exporting GA4 metrics by channel for ${account}`);
      const response = await this.api.get(`/google-analytics/ga4-aggregated/by-channel/${account}/export`, {
        params: { start_date: startDate, end_date: endDate },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to export GA4 metrics by channel:', error);
      throw error;
    }
  }

  async exportGA4ByDeviceCSV(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Exporting GA4 metrics by device for ${account}`);
      const response = await this.api.get(`/google-analytics/ga4-aggregated/by-device/${account}/export`, {
        params: { start_date: startDate, end_date: endDate },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to export GA4 metrics by device:', error);
      throw error;
    }
  }

  async exportGA4ByCountryCSV(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Exporting GA4 metrics by country for ${account}`);
      const response = await this.api.get(`/google-analytics/ga4-aggregated/by-country/${account}/export`, {
        params: { start_date: startDate, end_date: endDate },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to export GA4 metrics by country:', error);
      throw error;
    }
  }

  async exportGA4ByCityCSV(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Exporting GA4 metrics by city for ${account}`);
      const response = await this.api.get(`/google-analytics/ga4-aggregated/by-city/${account}/export`, {
        params: { start_date: startDate, end_date: endDate },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to export GA4 metrics by city:', error);
      throw error;
    }
  }

  async exportGA4BySourceMediumCSV(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Exporting GA4 metrics by source/medium for ${account}`);
      const response = await this.api.get(`/google-analytics/ga4-aggregated/by-source-medium/${account}/export`, {
        params: { start_date: startDate, end_date: endDate },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to export GA4 metrics by source/medium:', error);
      throw error;
    }
  }

  // Comparison Methods (Week-over-Week and Month-over-Month)
  async getGA4ComparisonWeek(account: string, endDate: string, aggregationType: string = 'by_page') {
    try {
      logger.debug(`Fetching GA4 week-over-week comparison for ${account}`);
      const response = await this.api.get(`/google-analytics/ga4-aggregated/comparison/week/${account}`, {
        params: { end_date: endDate, aggregation_type: aggregationType }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch GA4 week-over-week comparison:', error);
      throw error;
    }
  }

  async getGA4ComparisonMonth(account: string, endDate: string, aggregationType: string = 'by_page') {
    try {
      logger.debug(`Fetching GA4 month-over-month comparison for ${account}`);
      const response = await this.api.get(`/google-analytics/ga4-aggregated/comparison/month/${account}`, {
        params: { end_date: endDate, aggregation_type: aggregationType }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch GA4 month-over-month comparison:', error);
      throw error;
    }
  }

  async getGoogleAdsMetrics(account: string, startDate: string, endDate: string): Promise<GoogleAdsMetricsResponse> {
    try {
      logger.debug(`Fetching Google Ads metrics for ${account} from ${startDate} to ${endDate}`);
      return await this.cachedGet<GoogleAdsMetricsResponse>(`/google-analytics/google-ads-metrics/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch Google Ads metrics:', error);
      throw error;
    }
  }

  // =============================================================================
  // Google Search Console API Methods
  // =============================================================================

  async getSearchConsoleAccounts() {
    try {
      logger.debug('Fetching Search Console accounts');
      return await this.cachedGet('/search-console/accounts', undefined, 45000);
    } catch (error) {
      logger.error('Failed to fetch Search Console accounts:', error);
      throw error;
    }
  }

  async getSearchConsoleSummary(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Fetching Search Console summary for ${account} from ${startDate} to ${endDate}`);
      return await this.cachedGet(`/search-console/summary/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch Search Console summary:', error);
      throw error;
    }
  }

  async getSearchConsoleByQuery(account: string, startDate: string, endDate: string, limit: number = 100) {
    try {
      logger.debug(`Fetching Search Console queries for ${account}`);
      return await this.cachedGet(`/search-console/by-query/${account}`, {
        params: { start_date: startDate, end_date: endDate, limit }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch Search Console queries:', error);
      throw error;
    }
  }

  async getSearchConsoleByPage(account: string, startDate: string, endDate: string, limit: number = 100) {
    try {
      logger.debug(`Fetching Search Console pages for ${account}`);
      return await this.cachedGet(`/search-console/by-page/${account}`, {
        params: { start_date: startDate, end_date: endDate, limit }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch Search Console pages:', error);
      throw error;
    }
  }

  async getSearchConsoleByCountry(account: string, startDate: string, endDate: string, limit: number = 250) {
    try {
      logger.debug(`Fetching Search Console countries for ${account}`);
      return await this.cachedGet(`/search-console/by-country/${account}`, {
        params: { start_date: startDate, end_date: endDate, limit }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch Search Console countries:', error);
      throw error;
    }
  }

  async getSearchConsoleByDevice(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Fetching Search Console devices for ${account}`);
      return await this.cachedGet(`/search-console/by-device/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch Search Console devices:', error);
      throw error;
    }
  }

  async getSearchConsoleByDate(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Fetching Search Console daily data for ${account}`);
      const response = await this.api.get(`/search-console/by-date/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch Search Console daily data:', error);
      throw error;
    }
  }

  async getSearchConsoleWeekly(account: string, startDate: string, endDate: string) {
    try {
      logger.debug(`Fetching Search Console weekly data for ${account}`);
      return await this.cachedGet(`/search-console/weekly/${account}`, {
        params: { start_date: startDate, end_date: endDate }
      }, 30000);
    } catch (error) {
      logger.error('Failed to fetch Search Console weekly data:', error);
      throw error;
    }
  }

  async getSearchConsoleComparison(
    account: string,
    startDate: string,
    endDate: string,
    dimension: string = 'query',
    limit: number = 250
  ) {
    try {
      logger.debug(`Fetching Search Console comparison for ${account} by ${dimension}`);
      const response = await this.api.get(`/search-console/comparison/${account}`, {
        params: { start_date: startDate, end_date: endDate, dimension, limit }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch Search Console comparison:', error);
      throw error;
    }
  }

  // LinkedIn API Methods
  async getLinkedInAccounts() {
    try {
      logger.debug('Fetching LinkedIn accounts');
      const response = await this.api.get('/linkedin/ad-accounts');
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch LinkedIn accounts:', error);
      throw error;
    }
  }

  async syncLinkedInAdAccounts(account: string) {
    try {
      logger.debug(`Syncing LinkedIn ad accounts for ${account}`);
      const response = await this.api.post('/linkedin/sync/ad-accounts', null, {
        params: { account }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to sync LinkedIn ad accounts:', error);
      throw error;
    }
  }

  async syncLinkedInCampaignMetrics(account: string, params: any) {
    try {
      logger.debug(`Syncing LinkedIn campaign metrics for ${account}`, params);
      const response = await this.api.post('/linkedin/sync/campaign-metrics', null, { params: { account, ...params } });
      return response.data;
    } catch (error) {
      logger.error('Failed to sync LinkedIn campaign metrics:', error);
      throw error;
    }
  }

  async syncLinkedInLeadForms(account: string, params: any) {
    try {
      logger.debug(`Syncing LinkedIn lead forms for ${account}`, params);
      const response = await this.api.post('/linkedin/sync/lead-forms', null, { params: { account, ...params } });
      return response.data;
    } catch (error) {
      logger.error('Failed to sync LinkedIn lead forms:', error);
      throw error;
    }
  }

  async syncLinkedInOrganicPosts(account: string, params: { organization_id: string; max_posts?: number }) {
    try {
      logger.debug(`Syncing LinkedIn organic posts for ${account}`, params);
      const response = await this.api.post('/linkedin/sync/organic-posts', null, {
        params: {
          account,
          organization_id: params.organization_id,
          ...(params.max_posts ? { max_posts: params.max_posts } : {})
        }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to sync LinkedIn organic posts:', error);
      throw error;
    }
  }

  async getStoredLinkedInCampaignMetrics(params: any, forceRefresh: boolean = false) {
    try {
      const queryParams = { ...params };
      if (forceRefresh) {
        queryParams.force_refresh = 'true';
      }
      const queryString = new URLSearchParams(queryParams).toString();
      const response = await this.cachedGet(`/linkedin/stored/campaign-metrics?${queryString}`, undefined, 30000, forceRefresh);
      logger.debug('API Response - Campaign Metrics:', response);
      
      // Ensure proper structure
      if (!response || typeof response !== 'object') {
        return { campaign_metrics: [], total_records: 0 };
      }
      
      return response;
    } catch (error) {
      logger.error('Error fetching campaign metrics:', error);
      throw error; // Re-throw so Promise.allSettled catches it
    }
  }

  async getStoredLinkedInCampaignMetricsAggregated(params: any, forceRefresh: boolean = false) {
    try {
      const queryParams = { ...params };
      if (forceRefresh) {
        queryParams.force_refresh = 'true';
      }
      const queryString = new URLSearchParams(queryParams).toString();
      const response = await this.cachedGet(`/linkedin/stored/campaign-metrics/aggregated?${queryString}`, undefined, 30000, forceRefresh);
      logger.debug('API Response - Aggregated Campaign Metrics:', response);

      if (!response || typeof response !== 'object') {
        return { campaign_metrics: [], total_records: 0, summary: { ytd: 0, mtd: 0, wtd: 0 }, monthly_trend: [] };
      }

      return response;
    } catch (error) {
      logger.error('Error fetching aggregated campaign metrics:', error);
      throw error;
    }
  }

  async getStoredLinkedInOrganicPosts(params: {
    account: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
    forceRefresh?: boolean;
  }) {
    const forceRefresh = params.forceRefresh || false;
    const queryParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (key === 'forceRefresh') {
        return acc;
      }
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>);
    if (forceRefresh) {
      queryParams.force_refresh = 'true';
    }
    const queryString = new URLSearchParams(queryParams).toString();
    return this.cachedGet(`/linkedin/stored/organic-posts?${queryString}`, undefined, 30000, forceRefresh);
  }

  async listLinkedInSyncJobs(params?: {
    account?: string;
    job_type?: string;
    limit?: number;
    include_acknowledged?: boolean;
  }) {
    const response = await this.api.get('/linkedin/sync-jobs', {
      params,
    });
    return response.data;
  }

  async acknowledgeLinkedInSyncJob(jobId: string) {
    const response = await this.api.post(`/linkedin/sync-jobs/${jobId}/acknowledge`);
    return response.data;
  }

  async getStoredLinkedInLeadForms(params: {
    account: string;
    start_date?: string;
    end_date?: string;
    state?: string;
    forceRefresh?: boolean;
  }) {
    const forceRefresh = params.forceRefresh || false;
    const queryParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (key === 'forceRefresh') {
        return acc;
      }
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>);
    if (forceRefresh) {
      queryParams.force_refresh = 'true';
    }
    const queryString = new URLSearchParams(queryParams).toString();
    const response = await this.cachedGet(`/linkedin/stored/lead-forms?${queryString}`, undefined, 30000, forceRefresh);
    return response;
  }

async getStoredLinkedInLeadResponses(params: {
  account: string;
  start_date?: string;
  end_date?: string;
  form_id?: string;
  forceRefresh?: boolean;
}) {
  const forceRefresh = params.forceRefresh || false;
  const queryParams = Object.entries(params).reduce((acc, [key, value]) => {
    if (key === 'forceRefresh') {
      return acc;
    }
    if (value !== undefined && value !== null && value !== '') {
      acc[key] = String(value);
    }
    return acc;
  }, {} as Record<string, string>);
  if (forceRefresh) {
    queryParams.force_refresh = 'true';
  }
  const queryString = new URLSearchParams(queryParams).toString();
  const response = await this.cachedGet(`/linkedin/stored/lead-responses?${queryString}`, undefined, 30000, forceRefresh);
  return response;
}

  async syncLinkedInLeadResponses(account: string, params: any) {
    try {
      // Build query string from params
      const queryParams = new URLSearchParams({
        account,
        owner_type: params.owner_type,
        owner_id: params.owner_id,
        lead_type: params.lead_type || 'SPONSORED',
        test_leads_only: params.test_leads_only?.toString() || 'false'
      });

      // Add optional params
      if (params.start_date) {
        queryParams.append('start_date', params.start_date);
      }
      if (params.end_date) {
        queryParams.append('end_date', params.end_date);
      }
      if (params.form_id) {
        queryParams.append('form_id', params.form_id);
      }

      const response = await this.post(`/linkedin/sync/lead-responses?${queryParams.toString()}`, {});
      logger.debug('Sync Lead Responses Result:', response);
      return response;
    } catch (error) {
      logger.error('Error syncing lead responses:', error);
      throw error;
    }
  }

  async syncLinkedInFollowerStatistics(account: string, organization_id: string, start_date: string, end_date: string) {
    try {
      logger.debug(`Syncing LinkedIn follower statistics for ${account}`, { organization_id, start_date, end_date });
      const response = await this.api.post('/linkedin/sync/follower-statistics', null, {
        params: {
          account,
          organization_id,
          start_date,
          end_date
        }
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to sync LinkedIn follower statistics:', error);
      throw error;
    }
  }

  async getStoredLinkedInFollowerStatistics(params: {
    account: string;
    organization_id: string;
    start_date?: string;
    end_date?: string;
  }) {
    try {
      const queryString = new URLSearchParams(
        Object.entries(params).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            acc[key] = String(value);
          }
          return acc;
        }, {} as Record<string, string>)
      ).toString();
      const response = await this.cachedGet(`/linkedin/stored/follower-statistics?${queryString}`);
      logger.debug('API Response - Follower Statistics:', response);
      return response;
    } catch (error) {
      logger.error('Error fetching follower statistics:', error);
      throw error;
    }
  }

  async exportLinkedInLeadResponsesCSV(account: string) {
    try {
      logger.debug(`Exporting LinkedIn lead responses for account: ${account}`);
      const response = await this.api.get(`/linkedin/stored/lead-responses/export`, {
        params: { account },
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `linkedin_leads_${account}_${timestamp}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      logger.debug('LinkedIn lead responses exported successfully');
      return { success: true, message: 'Export completed successfully' };
    } catch (error) {
      logger.error('Error exporting LinkedIn lead responses:', error);
      throw error;
    }
  }


  // Generic CRUD methods for RBAC integration
  async get<T>(url: string, params?: any): Promise<T> {
    const response = await this.api.get(url, { params: this.buildSearchParams(params) });
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.api.post(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.api.put(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: any): Promise<T> {
    const response = await this.api.delete(url, config);
    return response.data;
  }

  // User Management & RBAC methods
  async initializeUser(userData: { email: string; display_name: string; firebase_uid: string }) {
    return this.post('/users/initialize', userData);
  }

  async createUser(userData: { email: string; display_name: string; role: string }) {
    return this.post('/users', userData);
  }

  async getCurrentUser() {
    return this.get('/users/me');
  }

  async updateCurrentUser(userData: any) {
    return this.put('/users/me', userData);
  }

  async getUsers(params?: any) {
    return this.get('/users', params);
  }

  async getUser(userId: string) {
    return this.get(`/users/${userId}`);
  }

  async updateUserRole(userId: string, role: string) {
    return this.put(`/users/${userId}/role`, { role });
  }

  async grantAccountAccess(userId: string, accountName: string) {
    return this.post(`/users/${userId}/accounts`, { account_name: accountName });
  }

  async revokeAccountAccess(userId: string, accountName: string) {
    return this.delete(`/users/${userId}/accounts/${accountName}`);
  }

  async registerUserLogin(userId: string) {
    return this.post(`/users/${userId}/login`);
  }

  async deleteUser(userId: string) {
    return this.delete(`/users/${userId}`);
  }

  async getUserPermissions(userId: string) {
    return this.get(`/users/${userId}/permissions`);
  }

  async checkPermission(permission: string, accountName?: string) {
    const params: any = { permission };
    if (accountName) params.account_name = accountName;
    return this.get('/permissions/check', params);
  }

  // Page Access methods
  async getAvailablePages() {
    return this.get('/pages/available');
  }

  async getUserPageAccess(email: string) {
    return this.get(`/users/${email}/page-access`);
  }

  async updateUserPageAccess(email: string, pages: string[]) {
    logger.log('API: Updating page access');
    const response = await this.put(`/users/${email}/page-access`, { pages });
    logger.log('API: Update page access completed');
    return response;
  }

  async grantPageAccess(email: string, pages: string[]) {
    return this.post(`/users/${email}/page-access`, { pages });
  }

  async revokePageAccess(email: string, pageId: string) {
    return this.delete(`/users/${email}/page-access/${pageId}`);
  }

  // System Monitoring methods (super-admin only)
  async getSystemMonitoringOverview(windowHours = 24) {
    return this.get('/system-monitoring/overview', { window_hours: windowHours });
  }

  async getSystemMonitoringServices() {
    return this.get('/system-monitoring/services');
  }

  async getSystemMonitoringLeadsWorker(windowHours = 24) {
    return this.get('/system-monitoring/leads-worker', { window_hours: windowHours });
  }

  async getSystemMonitoringWebhookProcessor(windowHours = 24) {
    void windowHours;
    return {
      status: 'disabled',
      message: 'Webhook processor monitoring has been retired',
    };
  }

  async getSystemMonitoringErrors(limit = 50, status?: 'all' | 'success' | 'failure' | 'denied') {
    const params: any = { limit };
    if (status) params.status = status;
    return this.get('/system-monitoring/errors', params);
  }

  async getSystemMonitoringMongoStatus() {
    return this.get('/system-monitoring/mongodb');
  }

  async getSystemMonitoringInfrastructure() {
    return this.get('/system-monitoring/infrastructure');
  }

  async getSystemMonitoringUsers(windowHours = 168) {
    return this.get('/system-monitoring/users', { window_hours: windowHours });
  }

  async getSystemMonitoringCloudAMQP() {
    return this.get('/system-monitoring/cloudamqp');
  }

  async getSystemMonitoringExternalWebhook() {
    return this.get('/system-monitoring/external-webhook');
  }

  async getSystemMonitoringCredentialsExpiry() {
    return this.get('/system-monitoring/credentials-expiry');
  }

  // Credential expiry notifications (for bell notifications)
  async getExpiringCredentialsNotifications(daysThreshold = 30) {
    return this.get('/notifications/expiring-credentials', { days_threshold: daysThreshold });
  }

  // Account Mapping methods
  async getAccountMappings(channel?: string, activeOnly?: boolean) {
    const params: any = {};
    if (channel) params.channel = channel;
    if (activeOnly !== undefined) params.active_only = activeOnly;
    return this.get('/account-mappings', params);
  }

  async createAccountMapping(mappingData: any) {
    return this.post('/account-mappings', mappingData);
  }

  async getGA4CountryRouteMappings(account: string) {
    return this.get(`/account-mappings/${encodeURIComponent(account)}/ga4-country-routes`);
  }

  async updateGA4CountryRouteMappings(
    account: string,
    mappings: Array<{ country_code: string; country_name: string }>
  ) {
    return this.put(`/account-mappings/${encodeURIComponent(account)}/ga4-country-routes`, { mappings });
  }

  // async getAccountMapping(mappingId: string) {
  //   return this.get(`/account-mappings/${mappingId}`);
  // }

  async updateAccountMapping(account: string, channel: string, external_id: string, mappingData: any) {
    return this.put(`/mappings/${account}/${channel}/${external_id}`, mappingData);
  }

  async deleteAccountMapping(account: string, channel: string, external_id: string) {
    return this.delete(`/mappings/${account}/${channel}/${external_id}`);
  }

  async resolveAccountName(channel: string, externalId: string) {
    return this.get(`/account-mappings/resolve/${channel}/${externalId}`);
  }

  async getChannelMappings(channel: string) {
    return this.get(`/account-mappings/channels/${channel}`);
  }

  async getChannelAccounts(channel: string) {
    return this.get(`/account-mappings/channels/${channel}/accounts`);
  }

  async invalidateMappingCache(pattern?: string) {
    return this.post('/account-mappings/cache/invalidate', { pattern });
  }

  async syncAccountConfiguration() {
    return this.post('/account-mappings/cache/invalidate');
  }

  // Prospects Search & Filters
  async getProspectsFilters() {
    return this.get('/prospects/filters');
  }

  async searchProspects(filters: {
    job_title?: string | string[];
    department?: string | string[];
    industry?: string | string[];
    city?: string | string[];
    country?: string | string[];
    account?: string | string[];
    email_status?: string | string[];
    source?: string | string[];
    lead_score?: string | string[];
    engagement?: string | string[];
    page?: number;
    page_size?: number;
  }) {
    return this.get('/prospects/search', filters);
  }

  async downloadProspects(filters: {
    job_title?: string | string[];
    department?: string | string[];
    industry?: string | string[];
    city?: string | string[];
    country?: string | string[];
    account?: string | string[];
    email_status?: string | string[];
    source?: string | string[];
    lead_score?: string | string[];
    engagement?: string | string[];
  }) {
    const formData = new FormData();
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((entry) => {
          if (entry) {
            formData.append(key, entry);
          }
        });
      } else if (value) {
        formData.append(key, value);
      }
    });

    const response = await this.api.post('/prospects/download', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob'
    });
    return response.data;
  }

  // ==================== Leads API ====================

  /**
   * Get leads with filtering and pagination
   */
  async getLeads(params?: {
    account?: string;
    stage?: string;
    qualified?: boolean;
    owner?: string;
    start_date?: string;
    end_date?: string;
    search?: string;
    page?: number;
    page_size?: number;
    sort_by?: string;
    sort_order?: string;
  }) {
    // Filter out undefined/null/empty values to prevent axios from sending empty query params
    const cleanParams = params ? Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ) : undefined;
    const response = await this.api.get('/leads', { params: cleanParams });
    return response.data;
  }

  /**
   * Get a single lead by ID
   */
  async getLead(leadId: string) {
    const response = await this.api.get(`/leads/${leadId}`);
    return response.data;
  }

  /**
   * Create a new lead
   */
  async createLead(leadData: {
    account: string;
    email: string;
    name?: string;
    designation?: string;
    company?: string;
    mobile_number?: string;
    message?: string;
    stage?: string;
    owner?: string;
    offering?: string;
    comments?: string;
    [key: string]: any;
  }) {
    const response = await this.api.post('/leads', leadData);
    return response.data;
  }

  /**
   * Update an existing lead
   */
  async updateLead(leadId: string, updates: {
    name?: string;
    designation?: string;
    company?: string;
    mobile_number?: string;
    message?: string;
    stage?: string;
    owner?: string;
    offering?: string;
    comments?: string;
    qualified?: boolean;
    qualified_date?: string;
    custom_fields?: {
      monthly_hiring?: string;
      low_high_volume?: string;
    };
    [key: string]: any;
  }) {
    const response = await this.api.put(`/leads/${leadId}`, updates);
    return response.data;
  }

  /**
   * Delete a lead (soft delete by default)
   */
  async deleteLead(leadId: string, hardDelete = false) {
    const response = await this.api.delete(`/leads/${leadId}`, {
      params: { hard_delete: hardDelete }
    });
    return response.data;
  }

  /**
   * Bulk update multiple leads
   */
  async bulkUpdateLeads(leadIds: string[], updates: Record<string, any>) {
    const response = await this.api.post('/leads/bulk-update', {
      lead_ids: leadIds,
      updates
    });
    return response.data;
  }

  /**
   * Bulk delete multiple leads
   */
  async bulkDeleteLeads(leadIds: string[]) {
    const response = await this.api.post('/leads/bulk-delete', {
      lead_ids: leadIds
    });
    return response.data;
  }

  /**
   * Upload leads from CSV/XLSX file
   */
  async uploadLeads(formData: FormData) {
    const response = await this.api.post('/leads/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Get lead upload job status
   */
  async getLeadUploadJobs(params?: {
    account?: string;
    status?: string;
    limit?: number;
    include_acknowledged?: boolean;
  }) {
    const response = await this.api.get('/leads/upload/jobs', { params });
    return response.data;
  }

  async acknowledgeLeadUploadJob(jobId: string) {
    const response = await this.api.post(`/leads/upload/jobs/${jobId}/acknowledge`);
    return response.data;
  }

  /**
   * Download leads as CSV or XLSX
   */
  async downloadLeads(params?: {
    account?: string;
    format?: 'csv' | 'xlsx';
    search?: string;
    stage?: string;
    qualified?: boolean;
    owner?: string;
  }) {
    const sanitizedParams = Object.fromEntries(
      Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );
    const response = await this.api.get('/leads/download', {
      params: sanitizedParams,
      responseType: 'blob'
    });
    return response.data;
  }

  /**
   * Get accounts available for leads (based on user access)
   */
  async getLeadAccounts() {
    const response = await this.api.get('/leads/accounts');
    return response.data;
  }

  /**
   * Get custom field configuration for an account
   */
  async getLeadCustomFields(account: string) {
    const response = await this.api.get(`/leads/custom-fields/${account}`);
    return response.data;
  }

  /**
   * Get lead statistics by account
   */
  async getLeadStats(account?: string) {
    const response = await this.api.get('/leads/stats', {
      params: account ? { account } : undefined
    });
    return response.data;
  }

  /**
   * Sync leads from external sources (MOFU scores, LinkedIn lead forms)
   */
  async syncLeads(account?: string, source?: 'mofu' | 'linkedin' | 'all') {
    const response = await this.api.post('/leads/sync', null, {
      params: {
        ...(account && { account }),
        ...(source && { source })
      }
    });
    return response.data;
  }

  async startDashboardRefreshJob(payload: {
    account: string;
    start_date: string;
    end_date: string;
    sources: string[];
  }) {
    const response = await this.api.post('/dashboard/refresh', payload);
    return response.data;
  }

  async getDashboardRefreshJob(jobId: string) {
    const response = await this.api.get(`/dashboard/refresh/${jobId}`);
    return response.data;
  }

  // ==========================================================================
  // Service Credentials Management (Admin Config Service)
  // ==========================================================================

  /**
   * Get all service credentials with optional channel filter
   * @param channel - Optional channel filter
   * @param decrypt - If true, return decrypted credential values for editing
   */
  async getServiceCredentials(channel?: string, decrypt?: boolean) {
    const params: Record<string, any> = {};
    if (channel) {
      params.channel = channel;
    }
    if (decrypt) {
      params.decrypt = 'true';
    }
    const response = await this.api.get('/admin/service-credentials', {
      params: Object.keys(params).length > 0 ? params : undefined
    });
    return response.data;
  }

  /**
   * Create a new service credential
   */
  async createServiceCredential(credential: {
    service_name?: string;
    channel: string;
    account: string;
    credential_type?: string;
    external_id?: string;
    credentials: Record<string, any>;
    is_active?: boolean;
    expires_at?: string;
    refresh_token_expires_at?: string;
    expiry_warning_days?: number;
    expiry_source?: 'auto' | 'manual';
    metadata?: Record<string, any>;
  }) {
    const response = await this.api.post('/admin/service-credentials', credential);
    return response.data;
  }

  /**
   * Update an existing service credential
   */
  async updateServiceCredential(credentialId: string, updates: {
    service_name?: string;
    credential_type?: string;
    external_id?: string;
    credentials?: Record<string, any>;
    is_active?: boolean;
    expires_at?: string;
    refresh_token_expires_at?: string;
    expiry_warning_days?: number;
    expiry_source?: 'auto' | 'manual';
    metadata?: Record<string, any>;
  }) {
    const response = await this.api.put(`/admin/service-credentials/${credentialId}`, updates);
    return response.data;
  }

  /**
   * Delete a service credential
   */
  async deleteServiceCredential(credentialId: string) {
    await this.api.delete(`/admin/service-credentials/${credentialId}`);
  }

  /**
   * Get credential health status (expiring credentials)
   */
  async getCredentialHealth(daysThreshold: number = 30) {
    const response = await this.api.get('/admin/service-credentials/health', {
      params: { days_threshold: daysThreshold }
    });
    return response.data;
  }

  /**
   * Refresh LinkedIn OAuth tokens
   */
  async refreshLinkedInToken() {
    const response = await this.api.post('/admin/service-credentials/linkedin/refresh-token');
    return response.data;
  }

  /**
   * Update credential expiry dates (for manual expiry entry)
   */
  async updateCredentialExpiry(credentialId: string, updates: {
    expires_at?: string;
    refresh_token_expires_at?: string;
    expiry_warning_days?: number;
    expiry_source?: 'auto' | 'manual';
  }) {
    const response = await this.api.post(`/admin/service-credentials/${credentialId}/update-expiry`, updates);
    return response.data;
  }

  /**
   * Invalidate credential cache (force hot-reload)
   */
  async invalidateCredentialCache(channel?: string, account?: string) {
    const response = await this.api.post('/admin/service-credentials/invalidate-cache', {
      channel,
      account
    });
    return response.data;
  }

  // ============================================================
  // Config Items API (for Other Configurations tab)
  // ============================================================

  /**
   * Get all config items, optionally filtered by category
   */
  async getConfigItems(category?: string) {
    const response = await this.api.get('/admin/config-items', {
      params: category ? { category } : {}
    });
    return response.data;
  }

  /**
   * Get a specific config item by category and key
   */
  async getConfigItem(category: string, configKey: string) {
    const response = await this.api.get(`/admin/config-items/${category}/${configKey}`);
    return response.data;
  }

  /**
   * Create a new config item
   */
  async createConfigItem(configItem: {
    category: string;
    config_key: string;
    config_value: unknown;
    value_type?: string;
    description?: string;
    is_secret?: boolean;
    metadata?: Record<string, unknown>;
  }) {
    const response = await this.api.post('/admin/config-items', configItem);
    return response.data;
  }

  /**
   * Update an existing config item
   */
  async updateConfigItem(category: string, configKey: string, updates: {
    config_value?: unknown;
    value_type?: string;
    description?: string;
    is_secret?: boolean;
    metadata?: Record<string, unknown>;
  }) {
    const response = await this.api.put(`/admin/config-items/${category}/${configKey}`, updates);
    return response.data;
  }

  /**
   * Delete a config item
   */
  async deleteConfigItem(category: string, configKey: string) {
    await this.api.delete(`/admin/config-items/${category}/${configKey}`);
  }

  /**
   * Bulk upsert config items
   */
  async bulkUpsertConfigItems(items: Array<{
    category: string;
    config_key: string;
    config_value: unknown;
    value_type?: string;
    description?: string;
    is_secret?: boolean;
    metadata?: Record<string, unknown>;
  }>) {
    const response = await this.api.post('/admin/config-items/bulk-upsert', { items });
    return response.data;
  }

  // ============================================
  // Pending User Management
  // ============================================

  /**
   * Get list of pending users awaiting approval
   */
  async getPendingUsers(): Promise<{ pending_users: Array<{
    email: string;
    display_name: string;
    uid: string;
    role: string;
    created_at: string;
  }> }> {
    const response = await this.api.get('/users/pending');
    return response.data;
  }

  /**
   * Get count of pending users
   */
  async getPendingUserCount(): Promise<{ count: number }> {
    const response = await this.api.get('/users/pending/count');
    return response.data;
  }

  /**
   * Approve a pending user
   */
  async approveUser(email: string, role: string = 'viewer'): Promise<{ message: string; user: any }> {
    const response = await this.api.post(`/users/${encodeURIComponent(email)}/approve`, { role });
    return response.data;
  }

  /**
   * Reject/delete a pending user
   */
  async rejectUser(email: string): Promise<void> {
    await this.api.delete(`/users/${encodeURIComponent(email)}/reject`);
  }
}

export const apiService = new ApiService();
export default apiService;
